import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import {
  DatePicker, Input, Button, Table, Space, Typography, Result, Spin,
  ConfigProvider, message, Layout,
} from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import ko_KR from 'antd/es/locale/ko_KR';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import '../styles/Common.css';
import { buildApiUrl } from '../config/api';
import { groupByEmployee } from '../utils/attendanceGrouping';

dayjs.extend(customParseFormat);
dayjs.locale('ko');

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Header, Content } = Layout;

const formatWorkMinutes = (m) => {
  if (m == null) return '-';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h && mm) return `${h}시간 ${mm}분`;
  if (h) return `${h}시간`;
  return `${mm}분`;
};

const dash = (v) => (v == null || v === '' ? '-' : v);

// 비회원 페이지: 로그인 토큰을 붙이지 않도록 인터셉터가 없는 순수 axios 사용
async function downloadFile(downloadUrl, filename) {
  try {
    const response = await fetch(downloadUrl);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('파일 다운로드 중 오류 발생:', error);
    message.error('엑셀 파일 다운로드 중 오류가 발생했습니다.');
  }
}

const AttendancePublicPage = () => {
  const [searchParams] = useSearchParams();
  const { slug } = useParams();
  // [v2] slug(useParams) 우선, 없으면 token(쿼리) 하위호환.
  const token = searchParams.get('token');
  // API 호출 시 붙일 접근 파라미터: slug 모드면 { slug }, 아니면 { token }
  const accessParams = useMemo(() => {
    if (slug) return { slug };
    if (token) return { token };
    return null;
  }, [slug, token]);

  const [contextLoading, setContextLoading] = useState(true);
  const [context, setContext] = useState(null);
  const [invalid, setInvalid] = useState(false);

  const [range, setRange] = useState([dayjs().startOf('month'), dayjs()]);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // [v2] 사람별 그룹핑(공용 유틸)
  const groups = useMemo(() => groupByEmployee(rows), [rows]);

  // 마운트 시 접근 키 검증
  useEffect(() => {
    let cancelled = false;
    const verify = async () => {
      if (!accessParams) {
        setInvalid(true);
        setContextLoading(false);
        return;
      }
      try {
        const { data } = await axios.get(buildApiUrl('/api/attendance-public/context'), {
          params: accessParams,
        });
        if (!cancelled) {
          setContext(data.data || null);
        }
      } catch (e) {
        if (!cancelled) setInvalid(true);
      } finally {
        if (!cancelled) setContextLoading(false);
      }
    };
    verify();
    return () => { cancelled = true; };
  }, [accessParams]);

  const fetchSummary = useCallback(async () => {
    if (!accessParams) return;
    if (!range || !range[0] || !range[1]) {
      message.warning('조회 기간을 선택하세요.');
      return;
    }
    const params = {
      ...accessParams,
      from: range[0].format('YYYY-MM-DD'),
      to: range[1].format('YYYY-MM-DD'),
    };
    if (q && q.trim()) params.q = q.trim();

    setLoading(true);
    try {
      const { data } = await axios.get(buildApiUrl('/api/attendance-public/summary'), { params });
      setRows(data.data || []);
    } catch (e) {
      if (e.response?.status === 401) {
        setInvalid(true);
      } else {
        message.error(e.response?.data?.message || '조회에 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  }, [accessParams, range, q]);

  const handleExport = async () => {
    if (!accessParams) return;
    if (!range || !range[0] || !range[1]) {
      message.warning('조회 기간을 선택하세요.');
      return;
    }
    const from = range[0].format('YYYY-MM-DD');
    const to = range[1].format('YYYY-MM-DD');
    const body = { ...accessParams, from, to };
    if (q && q.trim()) body.q = q.trim();

    setExporting(true);
    try {
      // 공개 엑셀은 단일 파일·기존 응답 shape 유지(files 배열 아님).
      const { data } = await axios.post(buildApiUrl('/api/attendance-public/summary/excel'), body);
      if (data.downloadUrl) {
        // [v3] 저장 파일명만 출퇴근 카드(아웃소싱)로 변경
        await downloadFile(data.downloadUrl, `출퇴근카드_아웃소싱_${from}~${to}.xlsx`);
      } else {
        message.error('다운로드 URL을 받지 못했습니다.');
      }
    } catch (e) {
      if (e.response?.status === 401) {
        setInvalid(true);
      } else {
        message.error(e.response?.data?.message || '엑셀 생성에 실패했습니다.');
      }
    } finally {
      setExporting(false);
    }
  };

  // 부모 행(사람 단위). 공개 페이지는 고용형태 열 생략(항상 아웃소싱).
  const parentColumns = [
    { key: 'name', title: '성명', dataIndex: 'name', width: 110, render: (v) => <Text strong>{dash(v)}</Text> },
    { key: 'department', title: '부서', dataIndex: 'department', width: 130, render: dash },
    { key: 'work_days', title: '근무일수', dataIndex: 'work_days', width: 100, render: (v) => `${v}일` },
    {
      key: 'total_work_minutes', title: '총 근무시간', dataIndex: 'total_work_minutes', width: 130,
      render: (v, record) => (record.has_work_minutes ? formatWorkMinutes(v) : '-'),
    },
  ];

  // 펼침(자식) 행: 일자별 상세
  const detailColumns = [
    { key: 'work_date', title: '근무일자', dataIndex: 'work_date', width: 120, render: dash },
    { key: 'check_in', title: '출근', dataIndex: 'check_in', width: 180, render: dash },
    { key: 'check_out', title: '퇴근', dataIndex: 'check_out', width: 180, render: dash },
    { key: 'work_minutes', title: '근무시간', dataIndex: 'work_minutes', width: 120, render: formatWorkMinutes },
  ];

  const expandedRowRender = (record) => (
    <Table
      dataSource={record.details}
      columns={detailColumns}
      rowKey={(r) => `${r.employee_id}-${r.work_date}`}
      size="small"
      pagination={false}
    />
  );

  if (contextLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="링크 확인 중..." />
      </div>
    );
  }

  if (invalid) {
    return (
      <Result
        status="403"
        title="접근할 수 없습니다"
        subTitle="유효하지 않거나 만료된 링크입니다."
      />
    );
  }

  const headerTitle = context?.vendor_name
    ? `${context.vendor_name} 출퇴근 조회`
    : '아웃소싱 출퇴근 조회';

  return (
    <ConfigProvider locale={ko_KR}>
      <Layout style={{ minHeight: '100vh' }}>
        <Header style={{ display: 'flex', alignItems: 'center', background: '#001529' }}>
          <Title level={4} style={{ color: '#fff', margin: 0 }}>{headerTitle}</Title>
        </Header>
        <Content style={{ padding: 24 }}>
          <div className="common-container">
            {context?.label && (
              <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                {context.label}
              </Text>
            )}
            <Space wrap style={{ marginBottom: 16 }}>
              <RangePicker
                value={range}
                onChange={(v) => setRange(v)}
                format="YYYY-MM-DD"
                allowClear={false}
              />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onPressEnter={fetchSummary}
                placeholder="직원 이름"
                allowClear
                style={{ width: 200 }}
              />
              <Button type="primary" icon={<SearchOutlined />} onClick={fetchSummary} loading={loading}>
                조회
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleExport} loading={exporting}>
                엑셀 다운로드
              </Button>
            </Space>
            <Table
              dataSource={groups}
              columns={parentColumns}
              rowKey="employee_id"
              loading={loading}
              size="small"
              scroll={{ x: 700 }}
              expandable={{ expandedRowRender }}
              pagination={{ pageSize: 20, showSizeChanger: true }}
            />
          </div>
        </Content>
      </Layout>
    </ConfigProvider>
  );
};

export default AttendancePublicPage;
